import React, { useEffect, useMemo, useState } from 'react';
import { p, tp, useP } from '../../i18n/useP';
import { useTranslation } from 'react-i18next';
import { usePersistentValue } from '../../hooks/usePersistentState';
import { peopleCostSynthesis, listHrExtraItems, listHrExpenseItems } from '../../api/hr';
import { formatNumber, formatMoney } from '../../utils/format';
import { normalizeName, buildSearchKey } from '../../utils/personTable';
import { useTableSort } from '../../hooks/useTableSort';
import PersonTh from '../../components/PersonTh';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import SegmentedToggle from '../../components/SegmentedToggle';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import './HrPage.css';

/* Catalogue complet de colonnes. Les groupes de colonnes (GROUPS ci-dessous)
   en sélectionnent des sous-ensembles, activables par puces. */
const COLUMNS = [
  { key: 'person_name',            label: p('Collaborateur'),          sortable: true, align: 'left',  numeric: false, personSort: true },
  { key: 'department',             label: tp('phrases:p_3d7c87c2af'),             sortable: true, align: 'left',  numeric: false },
  { key: 'status',                 label: p('Statut'),                 sortable: true, align: 'left',  numeric: false },
  { key: 'salary_base',            label: p('Salaires de base'),       sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_a57fdb4462') },
  { key: 'bonus_variable',         label: p('Extras annuels'),         sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_b8f8ec5412') },
  { key: 'effective_remuneration', label: p('Salaires bruts'),         sortable: true, align: 'right', numeric: true,
    title: p('Salaire de base + extras annuels (= brut soumis aux charges sociales)') },
  /* Décomposition de la charge employeur, en francs puis en taux. Le montant
     des assurances est le SOLDE de la cotisation hors LPP, jamais un produit
     taux × assiette : les deux taux sont arrondis au centième, et le détail
     doit se recomposer au franc (même règle que le plan LCR). */
  { key: 'cs_base_chf',            label: p('Cotisations de base'),    sortable: true, align: 'right', numeric: true,
    title: p('AVS / AI / APG, AC, allocations familiales, frais admin.') },
  { key: 'cs_ins_chf',             label: p('Assurances'),             sortable: true, align: 'right', numeric: true,
    title: p('AAP, AANP, LAA complémentaire, indemnités journalières — part employeur') },
  { key: 'lpp_empr_chf',           label: 'LPP',                    sortable: true, align: 'right', numeric: true,
    title: p('Prévoyance professionnelle, part employeur.') },
  { key: 'cs_empr_rate_pct',       label: 'CS empr. %',             sortable: true, align: 'right', numeric: true,
    title: p('Taux charges sociales employeur (hors LPP), du contrat ou valeur de repli de l\'entreprise.') },
  { key: 'cs_base_rate_pct',       label: p('Base %'),                 sortable: true, align: 'right', numeric: true,
    title: p('AVS / AI / APG, AC, allocations familiales, frais admin.') },
  { key: 'cs_ins_rate_pct',        label: p('Assurances %'),           sortable: true, align: 'right', numeric: true,
    title: p('AAP, AANP, LAA complémentaire, indemnités journalières — part employeur') },
  { key: 'lpp_empr_pct',           label: 'LPP empr. %',            sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_50c93ae037') },
  { key: 'total_empr_pct',         label: p('Total empr. %'),          sortable: true, align: 'right', numeric: true,
    title: p('CS empr. % + LPP empr. % (taux empr global, LPP comprise).') },
  { key: 'social_charges_empr',    label: p('Ch. sociales employeur'), sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_68b486b80c') },
  { key: 'cs_empl_rate_pct',       label: 'CS empl. %',             sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_fb626f4a26') },
  { key: 'lpp_empl_pct',           label: 'LPP empl. %',            sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_daefff70ba') },
  { key: 'total_empl_pct',         label: p('Total empl. %'),          sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_8789a30c91') },
  { key: 'social_charges_empl',    label: tp('phrases:p_40163a8ac9'),   sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_ef15d2ec91') },
  { key: 'loaded_payroll',         label: p('Salaires chargés'),       sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_d3b5f4afb2') },
  { key: 'fixed_indemnities',      label: 'Indem. fixes',           sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_dcda133948') },
  { key: 'paid_indemnities',       label: tp('phrases:p_973ea451b5'),         sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_da311dea84') },
  { key: 'other_expenses',         label: p('Autres frais'),           sortable: true, align: 'right', numeric: true,
    title: p('Autres frais. Source : Frais.') },
  { key: 'other_fees',             label: p('Total des frais'),        sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_c6f5a03884') },
  { key: 'total_cost_empr',        label: p('Charges de personnel totales'), sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_07f5f6dd25') },
  { key: 'estimated_net',          label: p('Salaires nets'),          sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_e418864ba6') },
  { key: 'received_indemnities',   label: tp('phrases:p_fe6550e5b1'),          sortable: true, align: 'right', numeric: true,
    title: tp('phrases:p_d8451f1175') },
];

/* Colonnes d'identité — toujours affichées, quel que soit le groupe actif. */
const BASE_COLS = ['person_name', 'department', 'status'];

/* Décomposition de la charge employeur, par ligne. Le montant des assurances
   est le SOLDE de la cotisation hors LPP (les deux taux sont arrondis au
   centième : calculés chacun de leur côté, base + assurances s'écarterait de
   la cotisation portée en paie). Quand le contrat porte son propre taux, le
   serveur ne dit PAS comment il se répartit : les deux postes restent absents
   plutôt qu'inventés, seule la LPP et le total se lisent. */
function splitEmployerCharges(r) {
  const rem = Number(r.effective_remuneration || 0);
  const csEmpr = rem * Number(r.cs_empr_rate_pct || 0) / 100;
  const known = r.cs_base_rate_pct != null;
  const base = known ? rem * Number(r.cs_base_rate_pct) / 100 : null;
  return {
    cs_base_chf:  base,
    cs_ins_chf:   known ? csEmpr - base : null,
    lpp_empr_chf: Number(r.social_charges_empr || 0) - csEmpr,
  };
}

/* Groupes de colonnes activables (puces). Une colonne s'affiche si elle
   appartient à au moins un groupe actif ; les chevauchements (ex. salaire brut,
   total des frais) sont dédupliqués via l'ordre canonique de COLUMNS. */
/* Un GROUPE de colonnes par TUILE de la synthèse (2026-08-27) : on clique le
   chiffre, la table montre d'où il vient. L'écran n'a plus d'onglets — la
   question « de quoi est fait ce montant » se pose tuile par tuile. */
const GROUPS = [
  { id: 'remuneration', label: p('Rémunération'),
    cols: ['salary_base', 'bonus_variable', 'effective_remuneration'],
    /* La colonne qui CONCLUT le groupe se lit en gras : les désignations
       d'extras s'intercalent avant elle, l'œil doit retrouver le total de
       ligne sans le chercher (2026-08-29). */
    result: 'effective_remuneration' },
  /* Les mêmes postes deux fois : en francs, puis en taux. Le salaire brut
     sort de la vue (2026-08-29) — il est l'assiette, pas une charge, et il se
     lit déjà dans la tuile Rémunération. */
  { id: 'socialEmpr', label: p('Charges sociales employeur'),
    cols: ['cs_base_chf', 'cs_ins_chf', 'lpp_empr_chf', 'social_charges_empr',
           'cs_base_rate_pct', 'cs_ins_rate_pct', 'lpp_empr_pct', 'total_empr_pct'],
    labels: { social_charges_empr: p('Total charges employeur') },
    result: 'social_charges_empr' },
  { id: 'loaded', label: p('Masse salariale chargée'),
    cols: ['effective_remuneration', 'social_charges_empr', 'loaded_payroll'] },
  { id: 'costEmpr', label: tp('phrases:p_4ff08f6137'),
    cols: ['loaded_payroll', 'other_fees', 'total_cost_empr'] },
  { id: 'expenses', label: tp('phrases:p_5c65373f2e'),
    cols: ['fixed_indemnities', 'paid_indemnities', 'other_expenses', 'other_fees'] },
  { id: 'net', label: p('Salaires nets'),
    cols: ['effective_remuneration', 'social_charges_empl', 'other_fees', 'estimated_net'] },
  { id: 'socialTotal', label: p('Charges sociales'),
    cols: ['effective_remuneration', 'social_charges_empr', 'social_charges_empl'] },
  { id: 'received', label: tp('phrases:p_fe6550e5b1'),
    cols: ['received_indemnities'] },
];

const STATUS_LABEL = {
  active:      { label: p('actif'),   cls: 'hr-status--active'   },
  joined:      { label: tp('phrases:p_c91a950272'),  cls: 'hr-status--joined'   },
  left:        { label: p('parti'),   cls: 'hr-status--left'     },
  left_before: { label: tp('phrases:p_1a79c7314a'),   cls: 'hr-status--inactive' },
  inactive:    { label: p('inactif'), cls: 'hr-status--inactive' },
};

/* Rendu d'une cellule selon la colonne — chaque clé a son renderer dédié. */
function renderCell(col, r, unit = 'chf', nameOrder = 'ln') {
  const k = col.key;
  const mny = (v) => formatMoney(v, unit, 0);   // montants (÷1000 si kCHF)
  // Colonne de désignation d'un complément : un montant comme un autre.
  if (k.startsWith('extra:') || k.startsWith('frais:')) {
    return <td className="hr-page__num">{mny(r[k])}</td>;
  }
  if (k === 'person_name') {
    return <td><PersonName firstName={r.first_name} lastName={r.last_name} order={nameOrder} /></td>;
  }
  if (k === 'department') {
    return <td className={r.department ? '' : 'hr-page__muted'}>{r.department || '—'}</td>;
  }
  if (k === 'status') {
    const s = STATUS_LABEL[r.status] || STATUS_LABEL.inactive;
    return <td><span className={`hr-status ${s.cls}`}>{s.label}</span></td>;
  }
  if (k === 'cs_empr_rate_pct') {
    return <td className="hr-page__num">{formatNumber(r.cs_empr_rate_pct, 2)}%</td>;
  }
  /* Répartition inconnue = case vide : le contrat porte son propre taux sans
     dire comment il se compose, et on n'invente pas une clé. */
  if (k === 'cs_base_chf' || k === 'cs_ins_chf') {
    if (r[k] == null) return <td className="hr-page__num hr-page__muted">—</td>;
    return <td className="hr-page__num">{mny(r[k])}</td>;
  }
  if (k === 'cs_base_rate_pct' || k === 'cs_ins_rate_pct') {
    if (r[k] == null) return <td className="hr-page__num hr-page__muted">—</td>;
    return <td className="hr-page__num">{formatNumber(r[k], 2)}%</td>;
  }
  if (k === 'lpp_empr_pct') {
    return (
      <td className="hr-page__num"
          style={r.lpp_from_fallback ? { background: 'var(--terracotta-light)', color: 'var(--terracotta-dark)', fontStyle: 'italic' } : undefined}
          title={r.lpp_from_fallback ? p('Aucun taux LPP saisi pour cette personne — taux par défaut tenant appliqué. À renseigner dans hr_lpp_rates.') : 'LPP empr CHF / remuneration'}>
        {formatNumber(r.lpp_empr_pct, 2)}%{r.lpp_from_fallback && ' ~'}
      </td>
    );
  }
  if (k === 'total_empr_pct') {
    return <td className="hr-page__num"><strong>{formatNumber(r.total_empr_pct, 2)}%</strong></td>;
  }
  if (k === 'cs_empl_rate_pct') {
    return <td className="hr-page__num">{formatNumber(r.cs_empl_rate_pct, 2)}%</td>;
  }
  if (k === 'lpp_empl_pct') {
    return <td className="hr-page__num">{formatNumber(r.lpp_empl_pct, 2)}%</td>;
  }
  if (k === 'total_empl_pct') {
    return <td className="hr-page__num"><strong>{formatNumber(r.total_empl_pct, 2)}%</strong></td>;
  }
  if (k === 'loaded_payroll') {
    return <td className="hr-page__num"><strong>{mny(r.loaded_payroll)}</strong></td>;
  }
  if (k === 'total_cost_empr') {
    return <td className="hr-page__num"><strong>{mny(r.total_cost_empr)}</strong></td>;
  }
  if (k === 'received_indemnities') {
    return (
      <td className={`hr-page__num ${Number(r.received_indemnities) ? '' : 'hr-page__zero'}`}
          style={Number(r.received_indemnities) ? { color: 'var(--sage-dark)' } : undefined}
          title={tp('phrases:p_6591b0ef8c')}>
        {mny(r.received_indemnities)}
      </td>
    );
  }
  if (k === 'bonus_variable' || k === 'other_fees' || k === 'fixed_indemnities' || k === 'paid_indemnities' || k === 'other_expenses') {
    return (
      <td className={`hr-page__num ${Number(r[k]) ? '' : 'hr-page__zero'}`}>
        {mny(r[k])}
      </td>
    );
  }
  // Default numeric
  if (col.strong) return <td className="hr-page__num"><strong>{mny(r[k])}</strong></td>;
  return <td className="hr-page__num">{mny(r[k])}</td>;
}

/* Rendu d'une cellule du tfoot (totaux). */
function renderTotalCell(col, totals, unit = 'chf') {
  const k = col.key;
  const mny = (v) => formatMoney(v, unit, 0);   // montants (÷1000 si kCHF)
  if (k === 'person_name') {
    return <td><strong>{p('Total / Moy.')}</strong></td>;
  }
  if (k === 'department') {
    return <td className="hr-page__muted">{totals.count} {totals.count > 1 ? p('collaborateurs') : p('collaborateur')}</td>;
  }
  if (k === 'status') {
    return <td></td>;
  }
  if (k === 'cs_empr_rate_pct') {
    return <td className="hr-page__num" title={tp('phrases:p_2168a00a4b')}><strong>{formatNumber(totals.avg_cs_empr_rate_pct, 2)}%</strong></td>;
  }
  if (k === 'cs_base_chf' || k === 'cs_ins_chf') {
    const v = k === 'cs_base_chf' ? totals.total_cs_base_chf : totals.total_cs_ins_chf;
    if (v == null) return <td className="hr-page__num hr-page__muted">—</td>;
    return <td className="hr-page__num"><strong>{mny(v)}</strong></td>;
  }
  if (k === 'cs_base_rate_pct' || k === 'cs_ins_rate_pct') {
    const v = k === 'cs_base_rate_pct' ? totals.avg_cs_base_rate_pct : totals.avg_cs_ins_rate_pct;
    if (v == null) return <td className="hr-page__num hr-page__muted">—</td>;
    /* Moyenne pondérée sur les SEULES lignes dont la répartition est connue :
       diviser par la masse entière ferait baisser un taux sans que rien ne le
       dise. */
    return <td className="hr-page__num" title={tp('phrases:p_2168a00a4b')}><strong>{formatNumber(v, 2)}%</strong></td>;
  }
  if (k === 'lpp_empr_pct') {
    return <td className="hr-page__num" title={tp('phrases:p_2168a00a4b')}><strong>{formatNumber(totals.avg_lpp_empr_pct, 2)}%</strong></td>;
  }
  if (k === 'total_empr_pct') {
    return <td className="hr-page__num" title={tp('phrases:p_2168a00a4b')}><strong>{formatNumber(totals.avg_total_empr_pct, 2)}%</strong></td>;
  }
  if (k === 'cs_empl_rate_pct') {
    return <td className="hr-page__num" title={tp('phrases:p_2168a00a4b')}><strong>{formatNumber(totals.avg_cs_empl_rate_pct, 2)}%</strong></td>;
  }
  if (k === 'lpp_empl_pct') {
    return <td className="hr-page__num" title={tp('phrases:p_2168a00a4b')}><strong>{formatNumber(totals.avg_lpp_empl_pct, 2)}%</strong></td>;
  }
  if (k === 'total_empl_pct') {
    return <td className="hr-page__num" title={tp('phrases:p_2168a00a4b')}><strong>{formatNumber(totals.avg_total_empl_pct, 2)}%</strong></td>;
  }
  if (k === 'received_indemnities') {
    return <td className="hr-page__num" style={{ color: 'var(--sage-dark)' }}><strong>{mny(totals.total_received_indemnities)}</strong></td>;
  }
  // Mapping clé colonne → clé total (Σ par défaut sur `total_<key>` ou `<key>` agrégé)
  const totalKeyMap = {
    salary_base:            'total_salary_base',
    bonus_variable:         'total_bonus_variable',
    effective_remuneration: 'total_effective_remuneration',
    social_charges_empr:    'total_social_empr',
    social_charges_empl:    'total_social_empl',
    loaded_payroll:         'total_loaded_payroll',
    fixed_indemnities:      'total_fixed_indemnities',
    paid_indemnities:       'total_paid_indemnities',
    other_expenses:         'total_other_expenses',
    other_fees:             'total_other_fees',
    total_cost_empr:        'total_cost_empr',
    estimated_net:          'total_estimated_net',
    lpp_empr_chf:           'total_lpp_empr_chf',
  };
  const tk = totalKeyMap[k];
  const v = tk ? totals[tk] : 0;
  return <td className="hr-page__num"><strong>{mny(v)}</strong></td>;
}

export default function HrSynthesisPage({ activeTenant, year: yearProp, onYearChange, embedded = false, unit = 'chf', exportAllGroups = false, group = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  // Année hissée dans App (persistée localStorage). Fallback local.
  const [localYear, setLocalYear] = useState(yearProp ?? new Date().getFullYear());
  const year = yearProp ?? localYear;
  const setYear = (y) => {
    const next = typeof y === 'function' ? y(year) : y;
    if (onYearChange) onYearChange(next);
    setLocalYear(next);
  };
  // Toggle Actifs / Tous — passé tel quel au backend ('active' | 'all').
  const [statusFilter, setStatusFilter] = usePersistentValue(`hr_synth_status_v2_${tenantId}`, 'all');
  const [search, setSearch] = useState('');
  const [groupMem, setActiveGroup] = usePersistentValue(`hr_synth_group_v2_${tenantId}`, 'remuneration');
  /* Compléments (hr_extra_items) : une ligne = une désignation × des mois de
     versement. La synthèse ne renvoie que leur SOMME (`bonus_variable`) ; on
     lit donc la source pour ouvrir une colonne par désignation, sinon
     « Extras » reste une boîte noire de quelques milliers de francs. */
  const [extraItems, setExtraItems] = useState([]);
  /* Même raison pour les FRAIS (hr_expense_items) : la synthèse n'en donne
     que le total, or ils se saisissent par désignation (forfait de
     représentation, véhicule, repas…). On lit la source pour ouvrir une
     colonne par désignation. */
  const [expenseItems, setExpenseItems] = useState([]);
  useEffect(() => {
    if (!tenantId || !year) { setExtraItems([]); setExpenseItems([]); return; }
    listHrExtraItems(tenantId, year)
      .then(r => setExtraItems(r?.data || r || []))
      .catch(() => setExtraItems([]));
    listHrExpenseItems(tenantId, year)
      .then(r => setExpenseItems(r?.data || r || []))
      .catch(() => setExpenseItems([]));
  }, [tenantId, year]);
  /* Total annuel par personne ET par désignation = montant × nombre de mois. */
  const byDesignation = (items, prefix, title) => {
    const byLabel = new Map();      // label → { personId → total }
    for (const it of (items || [])) {
      const label = (it.label || '').trim();
      if (!label) continue;
      const months = Array.isArray(it.payment_months) ? it.payment_months.length : 0;
      const total = Number(it.amount || 0) * months;
      if (!byLabel.has(label)) byLabel.set(label, {});
      const m = byLabel.get(label);
      m[it.person_id] = (m[it.person_id] || 0) + total;
    }
    return [...byLabel.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
      .map(([label, byPerson]) => ({
        key: `${prefix}:${label}`, label, sortable: true, align: 'right', numeric: true,
        title, byPerson,
      }));
  };
  const extraCols = useMemo(
    () => byDesignation(extraItems, 'extra',
                        p('Complément soumis aux charges sociales · source : Extras')),
    [extraItems]);
  const expenseCols = useMemo(
    () => byDesignation(expenseItems, 'frais', p('Frais par désignation · source : Frais')),
    [expenseItems]);
  /* `group` FIGE la vue : le hub « Détail des charges » en fait un onglet, et
     les puces « Afficher » n'ont alors plus lieu d'être (2026-08-27). */
  const activeGroup = group || groupMem;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const sort = useTableSort(`hr_synth_sort_${tenantId}`, 'last_name_sort', 'asc');
  const { sortCol, sortDir, handleSort, sortRows, nameOrder } = sort;

  // Colonnes affichées = identité + colonnes du groupe actif (un seul à la fois).
  const visibleCols = useMemo(() => {
    const grp = GROUPS.find(g => g.id === activeGroup) || GROUPS[0];
    const allowed = new Set([...BASE_COLS, ...grp.cols]);
    /* Les colonnes suivent l'ordre déclaré PAR LE GROUPE : le catalogue range
       par famille, une vue range par lecture (2026-08-29). */
    const byKey = new Map(COLUMNS.map(c => [c.key, c]));
    const order = [...BASE_COLS.filter(k => byKey.has(k)), ...grp.cols];
    const base = order
      .filter(k => allowed.has(k))
      .map(k => byKey.get(k))
      .filter(Boolean)
      .map(c => ({
        ...c,
        ...(grp.labels?.[c.key] ? { label: grp.labels[c.key] } : null),
        ...(c.key === grp.result ? { strong: true } : null),
      }));
    /* Rémunération : « Extras annuels » cède la place à UNE colonne par
       désignation. Sans désignation saisie, la colonne totale reste. */
    if (activeGroup === 'remuneration' && extraCols.length > 0) {
      const at = base.findIndex(c => c.key === 'bonus_variable');
      if (at >= 0) return [...base.slice(0, at), ...extraCols, ...base.slice(at + 1)];
    }
    /* Frais : les trois colonnes agrégées cèdent la place aux désignations,
       le total restant en fin de ligne. */
    if (activeGroup === 'expenses' && expenseCols.length > 0) {
      const keep = new Set([...BASE_COLS, 'other_fees']);
      const kept = base.filter(c => keep.has(c.key));
      const at = kept.findIndex(c => c.key === 'other_fees');
      return [...kept.slice(0, at), ...expenseCols, ...kept.slice(at)];
    }
    return base;
  }, [activeGroup, extraCols, expenseCols]);

  useEffect(() => {
    if (!tenantId) return;
    setLoading(true);
    peopleCostSynthesis(tenantId, year, statusFilter)
      .then(r => { if (!r.error) setData(r.data || r); })
      .finally(() => setLoading(false));
  }, [tenantId, year, statusFilter]);

  const enrichedRows = useMemo(() => {
    const rows = data?.rows || [];
    return rows.map(r => ({
      ...r,
      ...Object.fromEntries([...extraCols, ...expenseCols]
        .map(c => [c.key, c.byPerson[r.person_id] || 0])),
      person_name: `${r.last_name} ${r.first_name}`,
      first_name_sort: (r.first_name || '').toLowerCase(),
      last_name_sort:  (r.last_name  || '').toLowerCase(),
      _search: buildSearchKey(r.first_name, r.last_name, r.department),
      total_empr_pct: Number(r.cs_empr_rate_pct || 0) + Number(r.lpp_empr_pct || 0),
      ...splitEmployerCharges(r),
    }));
  }, [data, extraCols, expenseCols]);

  const filteredRows = useMemo(() => {
    const q = normalizeName(search.trim());
    if (!q) return enrichedRows;
    return enrichedRows.filter(r => r._search.includes(q));
  }, [enrichedRows, search]);

  // Recompute KPI totals from the filtered rows (so the strip adapts to dept filter).
  const totals = useMemo(() => {
    const sum = (k) => filteredRows.reduce((a, r) => a + Number(r[k] || 0), 0);
    const total_effective_remuneration = sum('effective_remuneration');
    // Weighted averages of the % columns, pondéré par la rémunération effective.
    // Évite la moyenne arithmétique qui surpondérait les petits salaires.
    const weightedAvg = (k) => {
      if (total_effective_remuneration <= 0) return 0;
      const num = filteredRows.reduce(
        (a, r) => a + Number(r[k] || 0) * Number(r.effective_remuneration || 0),
        0,
      );
      return num / total_effective_remuneration;
    };
    /* Répartition connue seulement : somme et moyenne se calculent sur ces
       lignes-là, et valent `null` quand aucune ne la porte. */
    const known = (k) => filteredRows.filter(r => r[k] != null);
    const sumKnown = (k) => {
      const rows = known(k);
      return rows.length ? rows.reduce((a, r) => a + Number(r[k]), 0) : null;
    };
    const weightedAvgKnown = (k) => {
      const rows = known(k);
      const base = rows.reduce((a, r) => a + Number(r.effective_remuneration || 0), 0);
      if (!rows.length || base <= 0) return null;
      return rows.reduce((a, r) => a + Number(r[k]) * Number(r.effective_remuneration || 0), 0) / base;
    };
    return {
      count:                          filteredRows.length,
      total_salary_base:              sum('salary_base'),
      total_bonus_variable:           sum('bonus_variable'),
      total_effective_remuneration,
      total_social_empr:              sum('social_charges_empr'),
      total_social_empl:              sum('social_charges_empl'),
      total_loaded_payroll:           sum('loaded_payroll'),
      total_fixed_indemnities:        sum('fixed_indemnities'),
      total_paid_indemnities:         sum('paid_indemnities'),
      total_other_expenses:           sum('other_expenses'),
      total_other_fees:               sum('other_fees'),
      total_received_indemnities:     sum('received_indemnities'),
      total_cost_empr:                sum('total_cost_empr'),
      total_estimated_net:            sum('estimated_net'),
      avg_cs_empr_rate_pct:           weightedAvg('cs_empr_rate_pct'),
      avg_lpp_empr_pct:               weightedAvg('lpp_empr_pct'),
      avg_total_empr_pct:             weightedAvg('total_empr_pct'),
      avg_cs_empl_rate_pct:           weightedAvg('cs_empl_rate_pct'),
      avg_lpp_empl_pct:               weightedAvg('lpp_empl_pct'),
      avg_total_empl_pct:             weightedAvg('total_empl_pct'),
      total_cs_base_chf:              sumKnown('cs_base_chf'),
      total_cs_ins_chf:               sumKnown('cs_ins_chf'),
      total_lpp_empr_chf:             sum('lpp_empr_chf'),
      avg_cs_base_rate_pct:           weightedAvgKnown('cs_base_rate_pct'),
      avg_cs_ins_rate_pct:            weightedAvgKnown('cs_ins_rate_pct'),
    };
  }, [filteredRows]);

  const sortedRows = useMemo(
    () => sortRows(filteredRows),
    [filteredRows, sortCol, sortDir], // eslint-disable-line
  );

  const currentYear = new Date().getFullYear();
  const yearOptions = [currentYear + 1, currentYear, currentYear - 1, currentYear - 2];

  if (!tenantId) return <PageShell embedded={embedded} framed={!embedded} title={t('phrases:p_06e3aaf141')}><div className="hr-page__empty">{p('Sélectionner une entreprise.')}</div></PageShell>;

  // ── KPI helpers ──────────────────────────────────────────────────────────
  // Note: totals are recomputed from filteredRows so the KPI strip adapts to dept filter.
  const gross = totals.total_effective_remuneration;
  const chfLbl = unit === 'kchf' ? 'kCHF' : 'CHF';   // suffixe d'unité des cartes synthèse
  const pct = (n) => gross ? ((Number(n) / gross) * 100) : 0;

  // Table « détail par personne » pour un jeu de colonnes donné (réutilisée pour
  // la vue active à l'écran et pour les 4 sous-vues de l'export Excel).
  const renderChargesTable = (cols, attrs = {}) => (
    <table className="hr-page__table" {...attrs}>
      <thead>
        <tr>
          {cols.map(col => {
            if (col.personSort) {
              return <PersonTh key={col.key} sort={sort} className="hr-page__sort-th hr-page__th-center" />;
            }
            const isSorted = sortCol === col.key;
            return (
              <th key={col.key} className={`hr-page__sort-th hr-page__th-center${isSorted ? ' is-sorted' : ''}`}
                onClick={() => handleSort(col.key)} title={col.title || (col.sortable ? p('Cliquer pour trier') : '')}>
                <span>{col.label}</span>
                <span className="hr-page__sort-ind">{isSorted ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(r => (
          <tr key={r.person_id}>
            {cols.map(col => <React.Fragment key={col.key}>{renderCell(col, r, unit, nameOrder)}</React.Fragment>)}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="hr-page__total-row">
          {cols.map(col => <React.Fragment key={col.key}>{renderTotalCell(col, totals, unit)}</React.Fragment>)}
        </tr>
      </tfoot>
    </table>
  );

  return (
    <PageShell
      embedded={embedded}
      framed={!embedded}
      title={t('phrases:p_06e3aaf141')}
    >
      {loading && <div className="hr-page__loading">{t('common:status.loading')}</div>}
      {!loading && data && (
        <div className="hr-charges-card hr-charges-card--bare">
          {/* ── Bandeau synthèse ─────────────────────────────────────────
              Chaque tuile est un BOUTON : on clique le chiffre, la table du
              dessous montre d'où il vient (2026-08-27). L'écran n'a plus
              d'onglets — la question se pose tuile par tuile. La première
              ligne pose l'équation : rémunération + charges sociales =
              masse salariale chargée. */}
          {/* Plus de bandeau de titre : la section s'appelle déjà « Détail des
              charges », et l'année vit dans la barre supérieure. */}
          <div className="hr-charges-card__body">
            <div className="hr-synth__eq">
              <KpiCell id="remuneration" label={p('Rémunération')}
                       value={formatMoney(totals.total_effective_remuneration, unit, 0)} suffix={chfLbl}
                       hint={p('salaire de base + extras')}
                       active={activeGroup === 'remuneration'} onPick={setActiveGroup} />
              <span className="hr-synth__op" aria-hidden>+</span>
              <KpiCell id="socialEmpr" label={p('Charges sociales employeur')}
                       value={formatMoney(totals.total_social_empr, unit, 0)} suffix={chfLbl}
                       hint={`${formatNumber(pct(totals.total_social_empr), 1)}% ${p('du brut')}`}
                       accent="terra"
                       active={activeGroup === 'socialEmpr'} onPick={setActiveGroup} />
              <span className="hr-synth__op" aria-hidden>=</span>
              <KpiCell id="loaded" label={p('Masse salariale chargée')}
                       value={formatMoney(totals.total_effective_remuneration + totals.total_social_empr, unit, 0)}
                       suffix={chfLbl}
                       hint={`+${formatNumber(pct(totals.total_social_empr), 1)}% ${p('vs brut')}`}
                       active={activeGroup === 'loaded'} onPick={setActiveGroup} />
              <span className="hr-synth__op" aria-hidden>+</span>
              <KpiCell id="expenses" label={t('phrases:p_5c65373f2e')}
                       value={formatMoney(totals.total_other_fees, unit, 0)} suffix={chfLbl}
                       hint={t('phrases:p_7dfd49b76b')}
                       active={activeGroup === 'expenses'} onPick={setActiveGroup} />
              <span className="hr-synth__op" aria-hidden>=</span>
              {/* Ce que l'entreprise DÉBOURSE pour son personnel : la masse
                  chargée plus les frais. Les indemnités reçues n'en sont pas
                  déduites — elles se lisent à part, ci-dessous. */}
              <KpiCell id="costEmpr" label={t('phrases:p_4ff08f6137')}
                       value={formatMoney(totals.total_cost_empr, unit, 0)} suffix={chfLbl}
                       hint={p('+{{pct}}% vs brut · indem. reçues exclues', { pct: formatNumber(pct(totals.total_cost_empr - gross), 1) })}
                       accent="primary"
                       active={activeGroup === 'costEmpr'} onPick={setActiveGroup} />
            </div>
            <div className="hr-synth__grid hr-synth__grid--3">
              <KpiCell id="net" label={p('Salaires nets')}
                       value={formatMoney(totals.total_estimated_net, unit, 0)} suffix={chfLbl}
                       hint={p('{{pct}}% du brut · avantages inclus', { pct: formatNumber(pct(totals.total_estimated_net), 1) })}
                       accent="sage"
                       active={activeGroup === 'net'} onPick={setActiveGroup} />
              <KpiCell id="socialTotal" label={p('Total charges sociales')}
                       value={formatMoney(totals.total_social_empr + totals.total_social_empl, unit, 0)}
                       suffix={chfLbl}
                       hint={`${formatNumber(pct(totals.total_social_empr + totals.total_social_empl), 1)}% · ${p('employeur + employé')}`}
                       accent="terra"
                       active={activeGroup === 'socialTotal'} onPick={setActiveGroup} />
              <KpiCell id="received" label={t('phrases:p_a3924bf10f')}
                       value={formatMoney(totals.total_received_indemnities, unit, 0)} suffix={chfLbl}
                       hint={t('phrases:p_6deef7c276')}
                       accent="sage"
                       active={activeGroup === 'received'} onPick={setActiveGroup} />
            </div>
          </div>
        </div>
      )}

      {!loading && data && (
        /* ── Table détail : entête standard de carte (2026-09-01) ────────── */
        <CardShell
          flush
          title={<>{p('Détail par personne')} <small>· {year} · {totals.count} {p('personne(s)')}</small></>}
          filters={(
            <>
              <FilterIcon />
              <SegmentedToggle
                compact
                label={p('Statut')} value={statusFilter} onChange={setStatusFilter}
                options={[
                  { value: 'all', label: p('Tous') },
                  { value: 'active', label: p('Actifs'), tone: 'alert' },
                ]}
              />
              <FilterCount count={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
                           onClear={() => { setStatusFilter('all'); setSearch(''); }} />
            </>
          )}
          search={(
            <SearchField value={search} onChange={setSearch}
                         placeholder={p('Rechercher…')} />
          )}
          filterCount={(statusFilter === 'active' ? 1 : 0) + (search.trim() ? 1 : 0)}
          collapsible storageKey="hr-charges-detail"
        >
          {totals.count === 0 && (
              <div className="hr-page__empty">
                Aucun collaborateur {statusFilter === 'active' ? 'actif ' : ''}pour {year}.
              </div>
            )}

            {totals.count > 0 && (
              exportAllGroups
                // Mode export Excel : les 4 sous-vues, chacune sa propre feuille.
                ? GROUPS.map(g => {
                    const allowed = new Set([...BASE_COLS, ...g.cols]);
                    const cols = COLUMNS.filter(c => allowed.has(c.key));
                    return (
                      <div className="hr-page__table-wrap" key={g.id}>
                        {renderChargesTable(cols, { 'data-export-name': g.label, 'data-export-sheet': '1' })}
                      </div>
                    );
                  })
                : <div className="hr-page__table-wrap">{renderChargesTable(visibleCols)}</div>
            )}
        </CardShell>
      )}
    </PageShell>
  );
}


/* Tuile de synthèse. Avec `id` + `onPick`, elle devient un BOUTON qui choisit
   ce que montre la table du dessous — le chiffre est la porte de son détail. */
function KpiCell({ label, value, hint, accent, negative, suffix, id, active, onPick }) {
  const cls = `hr-kpi ${accent ? `hr-kpi--${accent}` : ''}${onPick ? ' hr-kpi--pick' : ''}${active ? ' is-active' : ''}`;
  const inner = (
    <>
      <div className="hr-kpi__label">{label}</div>
      <div className={`hr-kpi__value${negative ? ' hr-kpi__value--negative' : ''}`}>
        {value}{suffix && <small className="hr-kpi__unit"> {suffix}</small>}
      </div>
      {hint && <div className="hr-kpi__hint">{hint}</div>}
    </>
  );
  if (!onPick) return <div className={cls}>{inner}</div>;
  return (
    <button type="button" className={cls} aria-pressed={!!active} onClick={() => onPick(id)}>
      {inner}
    </button>
  );
}
